import React from 'react';

import styles from './styles';

import {
  IMAGE_EXTENSIONS,
  type Media,
  type MediaEvent,
  MediaType,
} from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { useGetMediaView } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import { ContentLoader } from '@cobliteam/base-components';

import useMediaStore from '../../../../../../store/zustand/useMediaStore';

export interface MediaDialogPlayerProps {
  origin?: string;
  isFetchingMediaRows?: boolean;
}

const handleExtension = (url: string) =>
  IMAGE_EXTENSIONS.map((x) => url.includes(x)).reduce(
    (extension, hasIt) => extension || hasIt
  );

const handleVideoPlay = (mediaData: Media, path?: string) => {
  const mediaType = mediaData?.mediaType;
  const isFavorite = mediaData?.isFavorite;
  const eventType =
    mediaType === MediaType.EVENT_VIDEO
      ? (mediaData.mediaDetails as MediaEvent).eventTrigger
      : mediaType;
  const eventSeverity = mediaData.severity;

  Analytics.track(
    'Dashcam Video Played',
    eventSeverity
      ? { path, eventType, eventSeverity, isFavorite }
      : { path, eventType, isFavorite }
  );
};

const handleVideoEnded = (mediaData: Media, path?: string) => {
  const mediaType = mediaData?.mediaType;
  const eventType =
    mediaType === MediaType.EVENT_VIDEO
      ? (mediaData.mediaDetails as MediaEvent).eventTrigger
      : mediaType;
  const eventSeverity = mediaData.severity;

  Analytics.track(
    'Dashcam Video Watched Fully',
    eventSeverity ? { path, eventType, eventSeverity } : { path, eventType }
  );
};

const handleFullscreenVideo = () => {
  Analytics.track('Dashcam Video Fullscreen enabled');
};

const MediaDialogPlayer = ({
  origin,
  isFetchingMediaRows,
}: MediaDialogPlayerProps) => {
  const mediaViewId = useMediaStore((state) => state.mediaViewId);

  const { data: mediaView } = useGetMediaView(mediaViewId);

  const mediaData = mediaView?.mediaInfo;
  const mediaUrl = mediaView?.mediaUrl;

  if (!mediaData || !mediaUrl || isFetchingMediaRows) {
    return (
      <div className={styles.mediaBody}>
        <ContentLoader width={860} height={483}>
          <rect x='0' y='0' width='100%' height='100%' />
        </ContentLoader>
      </div>
    );
  }

  const handleMediaPlay = () => handleVideoPlay(mediaData, origin);

  const handleMediaEnded = () => handleVideoEnded(mediaData, origin);

  const renderMediaPlayer = () => {
    const isImage = handleExtension(mediaUrl);

    if (isImage) {
      return (
        <a href={mediaUrl} target='_blank' rel='noopener noreferrer'>
          <img src={mediaUrl} className={styles.mediaPlayer} alt='' />
        </a>
      );
    }

    return (
      <video
        controls
        key={mediaUrl}
        className={styles.mediaPlayer}
        onPlay={handleMediaPlay}
        onEnded={handleMediaEnded}
        onFocus={handleFullscreenVideo}
      >
        <source src={mediaUrl} type='video/mp4' />
      </video>
    );
  };

  return <div className={styles.mediaBody}>{renderMediaPlayer()}</div>;
};

export default MediaDialogPlayer;
